import React, { useState } from 'react';
import { referenceDocumentsApi } from '../../services/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  Label,
  Spinner,
} from '@/components/ui';
import type { ReferenceDocumentCard } from '../../types/panel-types';

interface PdfPasswordModalProps {
  document: ReferenceDocumentCard;
  onClose: () => void;
  onSuccess: () => void;
}

export default function PdfPasswordModal({ 
  document, 
  onClose, 
  onSuccess 
}: PdfPasswordModalProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Password is required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await referenceDocumentsApi.processWithPassword(document.documentId, password);
      onSuccess();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process document';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Determine if this is a retry (previous password was invalid)
  const isRetry = document.errorMessage?.toLowerCase().includes('invalid');

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>🔒</span> PDF Password Required
          </DialogTitle>
          <DialogDescription>
            "{document.title || 'This document'}" is password-protected. 
            Enter the password to continue processing.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {isRetry && (
            <div className="p-3 bg-destructive-light text-destructive rounded-lg text-sm">
              The previous password was incorrect. Please try again.
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="pdfPassword">PDF Password</Label>
            <Input
              id="pdfPassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter the PDF password"
              autoFocus
              autoComplete="off"
              disabled={loading}
            />
          </div>
          
          {error && (
            <div className="p-3 bg-destructive-light text-destructive rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !password.trim()}
            >
              {loading ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Processing...
                </>
              ) : (
                'Submit Password'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
